import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import './styles.css';

const TestimonialsSection = () => {
  const { t, i18n } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const isArabic = i18n.language === 'ar';
  
  // Données des témoignages (avec traduction)
  const testimonials = [
    {
      id: 1,
      name: isArabic ? "علي الصحراوي" : "Ali Sahraoui",
      role: isArabic ? "مستفيد من البرنامج" : "Bénéficiaire DNC",
      text: isArabic 
        ? "ساعدتني تجربتي مع برنامج الفرصة الجديدة في اكتشاف مجال الفندقة والسياحة، مما مكنني من فهم تحدياته وفرصه بشكل أعمق."
        : "Grâce à mon expérience avec le Dispositif Nouvelle Chance, j'ai découvert le domaine de l'hôtellerie et du tourisme et acquis une compréhension plus approfondie de leurs défis et opportunités.",
      image: "/image/temoignage/temoignage1.jpg",
      color: "#0f4c81"
    },
    {
      id: 2,
      name: isArabic ? "اناغيم زنيانا" : "Anaguim Zanina",
      role: isArabic ? "مسؤولة المرافقة" : "Chargée d'accompagnement",
      text: isArabic
        ? "لقد كان من دواعي سروري العمل مع الشباب ومساعدتهم على تطوير مهاراتهم والاستفادة من تجربتي معهم."
        : "J'ai vraiment apprécié travailler avec les jeunes et partager mes connaissances avec eux.",
      image: "/image/temoignage/temoignage2.jpg",
      color: "#ffd166"
    },
    {
      id: 3,
      name: isArabic ? "وفاء فتح الله" : "Ouafa Fathalah",
      role: isArabic ? "مسؤولة برنامج الفرصة الجديدة" : "Responsable DNC",    
      text: isArabic
        ? "مرافقة الشباب في رحلتهم نحو النجاح أصبحت ليس فقط عملي، بل شغفي الحقيقي."
        : "Accompagner les jeunes au quotidien, j'en ai fait mon métier et ma passion!",
      image: "/image/temoignage/temoignage3.JPG",
      color: "#0f4c81"
    },
    {
      id: 4,
      name: isArabic ? "مونيس بوعالغي" : "Moenes Bouallegui",
      role: isArabic ? "مستفيدة من البرنامج" : "Bénéficiaire DNC",
      text: isArabic
        ? "ساعدني برنامج الفرصة الجديدة على تحديد مساري المهني بوضوح واختيار التكوين المناسب لتحقيق طموحاتي."
        : "Grâce à l'IECD, j'ai gagné en clarté sur la carrière que je souhaite poursuivre et la formation dans laquelle je vais me spécialiser.",
      image: "/image/temoignage/temoignage4.jpg",
      color: "#ffd166"
    }
  ];

  // Fonction pour passer au témoignage suivant
  const nextTestimonial = () => {
    setActiveIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
  };

  // Fonction pour passer au témoignage précédent
  const prevTestimonial = () => {
    setActiveIndex((prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length);
  };

  // Effet pour changer automatiquement de témoignage toutes les 5 secondes
  useEffect(() => {
    // Référence à la fonction nextTestimonial pour éviter les problèmes de closure
    const handleAutoChange = () => nextTestimonial();
    const interval = setInterval(handleAutoChange, 5000);
    
    // Nettoyage complet lors du démontage du composant
    return () => {
      clearInterval(interval);
    };
  }, [testimonials.length]); // Dépendance à la longueur des témoignages

  return (
    <section className="testimonials-section">
      <div className="testimonials-container">
        <div className="testimonials-header">
          <motion.h2 
            className="testimonials-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {isArabic ? "شهادات" : "Témoignages"}
          </motion.h2>
          
          <motion.p 
            className="testimonials-subtitle"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {isArabic ? "تجارب حقيقية من المشاركين في برنامجنا" : "Des expériences réelles de participants à notre programme"}
          </motion.p>
        </div>
        
        <div className="testimonials-carousel">
          <button 
            className="testimonial-nav-button prev-button" 
            onClick={prevTestimonial}
            aria-label="Témoignage précédent"
          >
            
          </button>
          
          <motion.div 
            className="testimonial-card"
            key={activeIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.5 }}
          >
            <div className={`testimonial-content ${isArabic ? 'rtl-layout' : ''}`}>
              <div className="testimonial-image">
                <img 
                  src={testimonials[activeIndex].image} 
                  alt={testimonials[activeIndex].name} 
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/image/default.jpg';
                  }}
                />
              </div>
              
              <div className="testimonial-text">
                <div className="quote-icon" style={{ color: testimonials[activeIndex].color }}>“</div>
                <p className="quote-text">{testimonials[activeIndex].text}</p>
                <div className="quote-author" style={{ color: testimonials[activeIndex].color }}>
                  <h3>{testimonials[activeIndex].name}</h3>
                  <p>{testimonials[activeIndex].role}</p>
                </div>
              </div>
            </div>
          </motion.div>
          
          <button 
            className="testimonial-nav-button next-button" 
            onClick={nextTestimonial}
            aria-label="Témoignage suivant"
          >
           
          </button>
        </div>
        
        <div className="testimonial-dots">
          {testimonials.map((_, index) => (
            <button 
              key={index} 
              className={`testimonial-dot ${index === activeIndex ? 'active' : ''}`}
              onClick={() => setActiveIndex(index)}
              style={{ backgroundColor: index === activeIndex ? testimonials[index].color : '#ddd' }}
              aria-label={`Témoignage ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
